/* eslint-disable lingui/no-unlocalized-strings -- GA4 wire identifiers. */
import {
  ga4AcquisitionProperties,
  ga4ReferralProperties,
  sanitizeFirstTouch,
  UTM_KEYS,
} from "@reactive-resume/utils";

import { env } from "@/client/config/env";

import {
  getFirstTouch,
  getIncomingGa4ClientId,
  getLatestCampaign,
  setGa4HandoffId,
} from "./attribution";
import { analyticsAllowed, onConsentChange } from "./consent";
import { isTrackedEnvironment, safely } from "./environment";
import { resolveSite } from "./site";

type Gtag = (...args: unknown[]) => void;
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

let measurementId: string | undefined;
let listening = false;
let userId: string | null = null;
let currentPage: Record<string, unknown> | undefined;
let previousPage = "";
let scriptLoaded = false;

const loadScript = (id: string) => {
  if (scriptLoaded || document.getElementById("first-round-ga4")) return;
  const script = document.createElement("script");
  script.id = "first-round-ga4";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  script.onerror = () => {
    script.remove();
  };
  script.onload = () => {
    scriptLoaded = true;
    void getGa4Ids().then(() => window.dispatchEvent(new Event("ga4:ready")));
  };
  document.head.append(script);
};

/** Exclude OAuth codes, resume/interview IDs, free text and non-campaign query parameters. */
export const ga4PageLocation = (route: string) => {
  const url = new URL(window.location.href);
  const touch = sanitizeFirstTouch({
    captured_at: new Date().toISOString(),
    ...Object.fromEntries(UTM_KEYS.map((key) => [key, url.searchParams.get(key)])),
  });
  const query = new URLSearchParams();
  for (const key of UTM_KEYS) if (touch?.[key]) query.set(key, touch[key]!);
  return `${url.origin}${route}${query.size ? `?${query}` : ""}`;
};

const cleanReferrer = () => {
  try {
    const url = new URL(document.referrer);
    return `${url.origin}/`;
  } catch {
    return "";
  }
};

export const isJourneyReferrer = (referrer: string) => {
  try {
    const host = new URL(referrer).hostname;
    return (
      /(^|\.)(firstround\.com\.au|theresume\.io)$/.test(host) ||
      [
        "accounts.google.com",
        "checkout.stripe.com",
        "billing.stripe.com",
        "hooks.stripe.com",
      ].includes(host)
    );
  } catch {
    return false;
  }
};

const sendPage = () => {
  if (!measurementId || !analyticsAllowed() || !currentPage) return;
  window.gtag?.("set", currentPage);
  ga4Event("page_view", currentPage);
};

export const initGa4 = () => {
  if (typeof window === "undefined") return;
  if (!listening) {
    listening = true;
    onConsentChange((choice) => {
      const id = env.VITE_GA4_MEASUREMENT_ID;
      if (id)
        (window as unknown as Record<string, unknown>)[`ga-disable-${id}`] = choice === "denied";
      if (measurementId) {
        window.gtag?.("consent", "update", { analytics_storage: choice });
        if (choice === "granted") {
          window.gtag?.("set", { user_id: userId });
          sendPage();
        }
      } else if (choice === "granted") {
        initGa4();
        sendPage();
      }
    });
  }
  const id = env.VITE_GA4_MEASUREMENT_ID;
  if (!id || !isTrackedEnvironment() || !analyticsAllowed()) return;
  if (measurementId) {
    loadScript(id);
    return;
  }

  safely("ga4 init", () => {
    window.dataLayer = window.dataLayer ?? [];
    window.gtag =
      window.gtag ??
      function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer?.push(arguments);
      };
    window.gtag("consent", "default", {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    window.gtag("set", "linker", {
      domains: ["firstround.com.au", "www.firstround.com.au", "theresume.io", "www.theresume.io"],
      accept_incoming: true,
    });
    window.gtag("js", new Date());
    window.gtag("config", id, {
      send_page_view: false,
      user_id: userId,
      ...(getIncomingGa4ClientId() ? { client_id: getIncomingGa4ClientId() } : {}),
      ...(currentPage ?? {
        page_location: `${window.location.origin}/`,
        page_title: "First Round",
        page_referrer: cleanReferrer(),
      }),
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      ignore_referrer: isJourneyReferrer(document.referrer),
    });
    measurementId = id;
    loadScript(id);
  });
};

export const ga4Event = (name: string, params: Record<string, unknown> = {}) => {
  if (!measurementId || !analyticsAllowed()) return;
  safely(`ga4 ${name}`, () => {
    const acquisition = {
      ...ga4AcquisitionProperties(getFirstTouch()),
      ...ga4ReferralProperties(getLatestCampaign()),
    };
    window.gtag?.(
      "set",
      "user_properties",
      Object.fromEntries(
        Object.entries(acquisition).map(([key, value]) => [key, value.slice(0, 36)]),
      ),
    );
    window.gtag?.("event", name, {
      send_to: measurementId,
      site: resolveSite(window.location.hostname),
      ...acquisition,
      ...params,
    });
  });
};

export const ga4PageView = (route: string, name: string) => {
  const location = ga4PageLocation(route);
  currentPage = {
    page_location: location,
    page_title: name,
    page_route: route,
    page_name: name,
    page_referrer: previousPage || cleanReferrer(),
  };
  previousPage = location.split("?")[0];
  initGa4();
  sendPage();
};

/** An opaque internal ID; explicitly cleared when Clerk loses its session. */
export const ga4SetUser = (id: string | null) => {
  userId = id;
  if (measurementId && analyticsAllowed()) window.gtag?.("set", { user_id: id });
};

/** Use the supported API rather than parsing Google's changeable cookie formats. */
export const getGa4Ids = async (): Promise<{ clientId?: string; sessionId?: string }> => {
  initGa4();
  if (!measurementId || !analyticsAllowed()) return {};
  const get = (field: string) =>
    new Promise<unknown>((resolve) => {
      const timer = setTimeout(() => resolve(undefined), 1500);
      window.gtag?.("get", measurementId, field, (value: unknown) => {
        clearTimeout(timer);
        resolve(value);
      });
    });
  const [client, session] = await Promise.all([get("client_id"), get("session_id")]);
  if (!analyticsAllowed()) return {};
  if (typeof client === "string" && /^\d{1,20}\.\d{1,20}$/.test(client)) setGa4HandoffId(client);
  return {
    clientId:
      typeof client === "string" && /^\d{1,20}\.\d{1,20}$/.test(client)
        ? client
        : getIncomingGa4ClientId(),
    sessionId: /^\d{1,20}$/.test(String(session)) ? String(session) : undefined,
  };
};
